// packages/web/src/lib/swMessages.ts — CV6b: the app's ear for the worker.
//
// sw-push.js decides two things the APP has to carry out (see its header):
//
//   'refresh'  — a push landed for the thread you are looking at. It did not
//                buzz, deliberately; the screen must catch up or the app
//                quietly falls behind the conversation being read.
//   'navigate' — a human clicked a notification and THIS window is the one
//                that must go there. The worker never navigates a window
//                itself: inside an installed, chrome-less PWA a top-level
//                navigation has no way back (CV5 §1), so the app sets its
//                own hash instead.
//
// The `path` on the message is already the app's hash path (sw-push.js's
// hashPathOf) — CV6a serves the link in hash form and nothing re-derives it.
//
// THE TYPE STRING IS DECLARED TWICE, ON PURPOSE, AND PINNED BY A TEST.
// `public/sw-push.js` is served verbatim to the worker and is NOT part of
// this bundle's module graph (importing out of `public/` is exactly what
// Vite tells you not to do); so the constant is restated here and
// src/test/swPush.test.ts asserts the two are the same string. A drift that
// would otherwise be silent — the worker posting into a listener that no
// longer recognises it — fails a test instead.

/** Must equal sw-push.js's `SW_MESSAGE_TYPE`. */
export const SW_MESSAGE_TYPE = 'vowric-push';

export interface SwPushMessage {
  verb: 'refresh' | 'navigate';
  /** The full link, hash included, as the notification carried it. */
  link: string;
  /** The hash path alone — what `navigate()` takes. */
  path: string;
}

/** Subscribes to the worker's messages. Returns an unsubscribe. A browser
 * with no service worker is a no-op, never a crash. */
export function onServiceWorkerPushMessage(handler: (message: SwPushMessage) => void): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const listener = (event: MessageEvent) => {
    const data = event.data as Partial<SwPushMessage> & { type?: string };
    if (!data || data.type !== SW_MESSAGE_TYPE) return;
    if (data.verb !== 'refresh' && data.verb !== 'navigate') return;
    handler({ verb: data.verb, link: data.link ?? '/', path: data.path ?? '/' });
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}

/** The app's own route, in the form the worker compares against. */
export function currentHashPath(): string {
  if (typeof window === 'undefined') return '/';
  const hash = window.location.hash.replace(/^#/, '');
  if (hash === '') return '/';
  return hash.startsWith('/') ? hash : `/${hash}`;
}

/**
 * ANSWERS "WHERE ARE YOU?", and it is not optional.
 *
 * `WindowClient.url` does not track a hash change (measured in Chromium
 * 141 — sw-push.js's `whereQuery` carries the numbers), and Vowric is a
 * hash router, so the worker CANNOT tell which thread is open by looking.
 * It asks instead, over a MessagePort, and this is the reply. Without it
 * every push buzzes even while the human is reading the message, and the
 * open thread never refreshes.
 *
 * Installed at module scope from main.tsx rather than from a component: the
 * question can arrive at any moment, including while a route is mid-render,
 * and an unanswered query is a silently worse product rather than an error
 * anyone would notice.
 */
export function answerWorkerRouteQueries(): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const listener = (event: MessageEvent) => {
    const data = event.data as { type?: string; verb?: string } | null;
    if (!data || data.type !== SW_MESSAGE_TYPE || data.verb !== 'where') return;
    const port = event.ports?.[0];
    if (!port) return;
    port.postMessage({ path: currentHashPath() });
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}
